/**
 * ArticlesApi — Articles, Publishers, and unified Content-list methods.
 *
 * Extracted from client.ts (CUR-547 god-object split). Methods-only by
 * design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { Article, ArticleAnalysis, ArticleListParams, Publisher } from '../../../../shared/types/api';
import { APIError as SharedAPIError, DEFAULT_RETRY_OPTIONS, buildApiUrl, createAuthHeaders, processApiResponse, withRetry } from '../../../../shared/utils/api-helpers';
import { API_ENDPOINTS } from '@/utils/env';

import { BaseApiClient } from './base';

export class ArticlesApi extends BaseApiClient {
  // ============================================================================
  // Article Methods
  // ============================================================================

  async getArticles(params: ArticleListParams = {}): Promise<Article[]> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.articles, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchArticles = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<Article[]>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchArticles, DEFAULT_RETRY_OPTIONS);
  }

  async getArticlesCount(params: ArticleListParams = {}): Promise<{ total: number }> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.articles}/count`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchCount = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<{ total: number }>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchCount, DEFAULT_RETRY_OPTIONS);
  }

  async getArticle(id: number): Promise<Article> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.article(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<Article>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getAnalysisReadyArticles(params: ArticleListParams = {}): Promise<Article[]> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.articles}/analysis-ready`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchArticles = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<Article[]>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchArticles, DEFAULT_RETRY_OPTIONS);
  }

  async getArticleAnalysis(id: number): Promise<ArticleAnalysis> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.articles}/${id}/analysis`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<ArticleAnalysis>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Publisher Methods
  // ============================================================================

  async getPublishers(): Promise<Publisher[]> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.publishers);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<Publisher[]>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Content Methods (Unified)
  // ============================================================================

  async getContent(params: any = {}): Promise<any[]> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.content, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchContent = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<any[]>(response);
      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }
      return result.data!;
    };

    return withRetry(fetchContent, DEFAULT_RETRY_OPTIONS);
  }

  /** One page of content plus the exact match count from `X-Total-Count` (CUR-2078). */
  async getContentPage(params: any = {}): Promise<{ items: any[]; totalCount: number | null }> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.content, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchPage = async () => {
      const response = await fetch(url, { headers });
      const header = response.headers.get('X-Total-Count');
      const result = await processApiResponse<any[]>(response);
      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }
      return { items: result.data!, totalCount: header === null ? null : Number(header) };
    };

    return withRetry(fetchPage, DEFAULT_RETRY_OPTIONS);
  }

  async getContentProgress(contentId: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/progress`);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchProgress = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<any>(response);
      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }
      return result.data!;
    };

    return withRetry(fetchProgress, DEFAULT_RETRY_OPTIONS);
  }


  async getContentDetail(contentId: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}`);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

  async getContentAnalysis(contentId: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/analysis`);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data!;
  }

}
